import {
  Component,
  Event,
  type EventEmitter,
  h,
  Prop,
  State,
  Watch,
  Listen,
} from '@stencil/core';
import { type FacetChange } from '../../FacetChange';
import { type FacetPriceRange } from '../../FacetPriceRange';
import { makeLogger } from '../../../../../logger/logger';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';

const { info } = makeLogger({
  pathToCurrentFile:
    'components/result-page/facets/subcomponents/range-slider/svs-range-slider.tsx',
});
@Component({
  tag: 'svs-range-slider',
  styleUrl: 'svs-range-slider.scss',
  shadow: true,
})
export class RangeSliderComponent {

  /**
   * The minimum available value to select
   */
  @Prop() readonly minValue: number = 0;

  /**
   * The maximum available value to select
   */
  @Prop() readonly maxValue: number = 10;

  /**
   * Debounce time for delaying the value change on the slider after entering the value in the input.
   * Prevents slider jumps when entering values in the input.
   */
  @Prop() readonly debounceTime: number = 500;
  /**
   * The color of the range between values
   */
  @Prop() readonly rangeSliderColor: string = '#626469';
  /**
   * The price currency
   */
  @Prop() readonly currency: string = '$';
  /**
   * The background color of the slider
   */
  @Prop() readonly backgroundSliderColor: string = '#F7F7F7';

  @Prop({ mutable: true }) labelProvider?: labelProviderInterface;

  @State() fromSliderValue = this.getSliderValue(this.minValue, 0);
  @State() toSliderValue = this.getSliderValue(this.maxValue, 100);
  @State() fromInputValue = '';
  @State() toInputValue = '';
  private toSliderEl: HTMLDivElement;
  private fromSliderEl: HTMLDivElement;
  @State() isInputMouseLeave = false;
  private newInputVal = null;
  private isRequestSent = false;
  private isReset = false;
  private timerFrom: number;
  private timerTo: number;

  /**
   * Emit event to update facets state.
   */
  @Event({
    eventName: 'search:facetRangeChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    facetRangeChanged: EventEmitter<FacetChange>;

  /**
   * Listen on filter reset event.
   */
  @Listen('search:reset', { target: 'document' })
  reset() {
      this.fromSliderValue = this.minValue.toString();
      this.toSliderValue = this.maxValue.toString();
      this.fromInputValue = '';
      this.toInputValue = '';
      this.isReset = true;
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            info('LabelProvider not provided in svs-sorting-dropdown component', [
              name,
              fallback,
            ]);
            return fallback;
        };
      }
  }

  componentDidLoad() {
      if (this.toSliderEl)
        this.fillSlider(
          this.backgroundSliderColor,
          this.rangeSliderColor,
          this.toSliderEl
        );
  }

  private getSliderValue(value, fallbackValue) {
      // value can be a number or string
      if(value && !isNaN(value)) {
        return value.toString();
      } else {
        return fallbackValue;
      }
  }

  private controlFromInput(fromInput, toInput) {
      const [from, to] = this.getParsed(fromInput, toInput);
      if (from < this.minValue) {
        this.fromSliderValue = this.minValue.toString();
        this.fromInputValue = this.minValue.toString();
      } else if (from > to) {
        this.fromInputValue = to.toString();
      } else {
        this.fromSliderValue = from.toString();
        (this.fromSliderEl as HTMLInputElement).value = from.toString();
      }
  }

  private controlToInput(fromInput, toInput, toSliderEl) {
      const [from, to] = this.getParsed(fromInput, toInput);
      this.setToggleAccessible(toInput, toSliderEl);
      if (to > this.maxValue) {
        this.toSliderValue = this.maxValue.toString();
        this.toInputValue = this.maxValue.toString();
      } else if (from > to) {
        this.toInputValue = from.toString();
      } else {
        this.toSliderValue = to.toString();
        (this.toSliderEl as HTMLInputElement).value = to.toString();
      }
  }

  private controlFromSlider(fromSlider, toSlider) {
      const [from, to] = this.getParsed(fromSlider, toSlider);
      if (from > to) {
        (this.fromSliderEl as HTMLInputElement).value = to.toString();
        this.fromInputValue = to.toString();
      } else {
        this.fromInputValue = from.toString();
      }
  }

  private controlToSlider(fromSlider, toSlider, toSliderEl) {
      const [from, to] = this.getParsed(fromSlider, toSlider);
      this.setToggleAccessible(toSlider, toSliderEl);
      if (from > to) {
        (this.toSliderEl as HTMLInputElement).value = from.toString();
        this.toInputValue = from.toString();
      } else {
        this.toInputValue = to.toString();
      }
  }

  private handleFromSliderChange(fromSliderValue, toSliderEl, toSliderValue) {
      this.fromSliderValue = fromSliderValue;
      this.fillSlider(
        this.backgroundSliderColor,
        this.rangeSliderColor,
        toSliderEl
      );
      this.controlFromSlider(fromSliderValue, toSliderValue);
  }

  private handleToSliderChange(fromSliderValue, toSliderEl, toSliderValue) {
      this.toSliderValue = toSliderValue;
      this.fillSlider(
        this.backgroundSliderColor,
        this.rangeSliderColor,
        toSliderEl
      );
      this.controlToSlider(fromSliderValue, toSliderValue, toSliderEl);
  }

  private sendFacetRequest(val, newVal) {
      // newVal can be string or boolean
      if (typeof newVal === 'string') {
        this.newInputVal = newVal;
        this.isRequestSent = true;
      }

      const obj: FacetPriceRange = {
        rangeToValue: this.toSliderValue,
        priceKey: 'price::',
        fieldValue: this.fromSliderValue,
        displayName: '',
        id: 'price',
        selected: false,
        value: this.fromSliderValue,
        count: null,
        children: [],
        uniqueId: `price::${this.fromSliderValue}`,
      };

      if (
        val === this.newInputVal &&
      this.isRequestSent &&
      this.isInputMouseLeave &&
      !this.isReset
      ) {
        this.facetRangeChanged.emit(obj as unknown as FacetChange);
        this.isRequestSent = false;
      }
      const emptyValues = this.fromInputValue === '' && this.toInputValue === '';
      if (!emptyValues) this.isReset = false;
  }

  disconnectedCallback() {
      if (this.timerFrom) window.clearTimeout(this.timerFrom);
      if (this.timerTo) window.clearTimeout(this.timerTo);
  }

  @Watch('minValue')
  updateFromSlider(val) {
      if(this.isReset) {
        this.timerFrom = window.setTimeout(() => {
            this.handleFromInputChange(
              val.toString(),
              this.toSliderEl,
              ''
            );
        }, 0);
      } else {
        this.fillSlider(
          this.backgroundSliderColor,
          this.rangeSliderColor,
          this.toSliderEl
        );
      }
  }

  @Watch('maxValue')
  updateToSlider(val) {
      if(this.isReset) {
        this.timerTo = window.setTimeout(() => {
            this.handleToInputChange(
              '',
              this.toSliderEl,
              val.toString(),
            );
        }, 0);
      } else {
        this.fillSlider(
          this.backgroundSliderColor,
          this.rangeSliderColor,
          this.toSliderEl
        );
      }
  }

  @Watch('fromInputValue')
  @Watch('isInputMouseLeave')
  updateFromValue(newVal) {
      setTimeout(() => {
          this.handleFromInputChange(
            this.fromInputValue,
            this.toSliderEl,
            this.toInputValue
          );
          setTimeout(() => this.sendFacetRequest(this.fromInputValue, newVal), this.debounceTime);
      }, this.debounceTime);
  }

  @Watch('toInputValue')
  @Watch('isInputMouseLeave')
  updateToValue(newVal) {
      setTimeout(() => {
          this.handleToInputChange(
            this.fromInputValue,
            this.toSliderEl,
            this.toInputValue
          );
          setTimeout(() => this.sendFacetRequest(this.toInputValue, newVal), this.debounceTime);
      }, this.debounceTime);
  }

  private handleFromInputChange(fromInputValue, toSliderEl, toInputValue) {
      const fromValue = fromInputValue || this.minValue;
      this.controlFromInput(fromValue, toInputValue);
      this.fillSlider(
        this.backgroundSliderColor,
        this.rangeSliderColor,
        toSliderEl
      );
  }

  private handleToInputChange(fromInputValue, toSliderEl, toInputValue) {
      const toValue = toInputValue || this.maxValue;
      this.controlToInput(fromInputValue, toValue, toSliderEl);
      this.fillSlider(
        this.backgroundSliderColor,
        this.rangeSliderColor,
        toSliderEl
      );
  }

  private getParsed(currentFrom, currentTo) {
      const from = parseInt(currentFrom, 10);
      const to = parseInt(currentTo, 10);
      return [from, to];
  }

  private fillSlider(sliderColor, rangeColor, controlSlider) {
      const rangeDistance = this.maxValue - this.minValue;
      const fromPosition = parseInt(this.fromSliderValue, 10) - this.minValue;
      const toPosition = parseInt(this.toSliderValue, 10) - this.minValue;

      return (controlSlider.style.background = `linear-gradient(
      to right,
      ${sliderColor} 0%,
      ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(toPosition / rangeDistance) * 100}%,
      ${sliderColor} ${(toPosition / rangeDistance) * 100}%,
      ${sliderColor} 100%)`);
  }

  private setToggleAccessible(currentTarget, toSliderEl) {
      if (Number(currentTarget) <= this.minValue) {
        toSliderEl.style.zIndex = '2';
      } else {
        toSliderEl.style.zIndex = '0';
      }
  }

  render() {
      return (
        <div
          class="range_container"
          onMouseLeave={(e) => {
              e.stopPropagation();
              this.isInputMouseLeave = true;
          }}
          onMouseEnter={(e) => {
              e.stopPropagation();
              this.isInputMouseLeave = false;
          }}
          // events for mobile
          onTouchEnd={(e) => {
              e.stopPropagation();
              this.isInputMouseLeave = true;
          }}
          onTouchStart={(e) => {
              e.stopPropagation();
              this.isInputMouseLeave = false;
          }}
        >
          <div class="sliders_control">
            <input
              ref={(el) => (this.fromSliderEl = el as HTMLDivElement)}
              onInput={(e) =>
                this.handleFromSliderChange(
                  (e.currentTarget as HTMLInputElement).value,
                  this.toSliderEl,
                  this.toSliderValue
                )
              }
              id="fromSlider"
              type="range"
              min={this.minValue}
              max={this.maxValue}
              defaultValue={this.fromSliderValue}
            />
            <input
              ref={(el) => (this.toSliderEl = el as HTMLDivElement)}
              onInput={(e) =>
                this.handleToSliderChange(
                  this.fromSliderValue,
                  this.toSliderEl,
                  (e.currentTarget as HTMLInputElement).value
                )
              }
              id="toSlider"
              type="range"
              min={this.minValue}
              max={this.maxValue}
              defaultValue={this.toSliderValue}
            />
          </div>
          <div class="form_control">
            <div class="from-input__wrapper">
              <input
                onInput={(e) => {
                    this.fromInputValue = (
                  e.currentTarget as HTMLInputElement
                    ).value;
                }}
                class="form_control_container__price__input"
                type="number"
                id="fromInput"
                min={this.minValue}
                max={this.maxValue}
                value={this.fromInputValue}
                placeholder={`${this.labelProvider('min', 'min')} (${this.currency}${this.minValue})`}
              />
              <div
                class={
                  this.fromInputValue
                    ? 'input-currency--visible'
                    : 'input-currency--hidden'
                }
              >
                {this.currency}
              </div>
            </div>
            <div class="to-input__wrapper">
              <input
                onInput={(e) => {
                    this.toInputValue = (e.currentTarget as HTMLInputElement).value;
                }}
                class="form_control_container__price__input"
                type="number"
                id="toInput"
                min={this.minValue}
                max={this.maxValue}
                value={this.toInputValue}
                placeholder={`${this.labelProvider('max', 'max')} (${this.currency}${this.maxValue})`}
              />
              <div
                class={
                  this.toInputValue
                    ? 'input-currency--visible'
                    : 'input-currency--hidden'
                }
              >
                {this.currency}
              </div>
            </div>
          </div>
        </div>
      );
  }
}
